// components/PendingTasksPanel.tsx
// 未処理タスク一覧パネル（医師・管理員共用）
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, RefreshCw, CheckCircle2, Package, Bell } from 'lucide-react';
import api from '../api/axios';
import { fetchPendingTasks, type PendingTasksData, type TaskItem } from '../api/pendingTasks';

// 新規予約通知のポーリング間隔（ms）
const POLL_INTERVAL_MS = 60_000;
// 増加を検出してバナーを出すカテゴリキー（予約 + 追加配送）
const NEW_RESERVATION_KEYS: (keyof PendingTasksData)[] = [
  'today_consultations',
  'unassigned_pool',
  'shipping_pending',   // 追加配送（paid 後 shipping_pending に入る）
];

interface Props {
  role: 'doctor' | 'admin';
  onClaimSuccess?: () => void;
}

// バッジカラー：件数に応じて変化
function badgeClass(count: number): string {
  if (count === 0) return 'bg-gray-100 text-gray-400';
  if (count < 5)   return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-600 font-bold';
}

const PendingTasksPanel: React.FC<Props> = ({ role, onClaimSuccess }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState<PendingTasksData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // 展開中のカテゴリ
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set());
  // claim モーダル
  const [claimTarget, setClaimTarget] = useState<TaskItem | null>(null);
  const [claiming, setClaiming] = useState(false);
  // 追跡番号インライン入力
  const [trackingItemId, setTrackingItemId] = useState<number | null>(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingCarrier, setTrackingCarrier] = useState('yamato');
  const [trackingSubmitting, setTrackingSubmitting] = useState(false);
  // 二重送信防止：React state は非同期バッチのため ref で即時フラグ管理
  const trackingSubmittingRef = useRef(false);
  // 初回自動展開フラグ（stale closure を避けるため useRef で管理）
  const autoExpandedRef = useRef(false);
  // ポーリング用：前回取得時のカテゴリ件数スナップショット
  const prevCountsRef = useRef<Partial<Record<keyof PendingTasksData, number>>>({});
  // 新規通知バナー（種別：予約 or 追加配送）
  const [newReservationBanner, setNewReservationBanner] = useState(false);
  const [bannerType, setBannerType] = useState<'reservation' | 'refill' | 'both'>('reservation');

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(false);
    try {
      const d = await fetchPendingTasks();

      // ── 新規予約検出（ポーリング時のみ）────────────────────────────
      if (silent && autoExpandedRef.current) {
        const gained = NEW_RESERVATION_KEYS.filter(
          key => (d[key]?.count ?? 0) > (prevCountsRef.current[key] ?? 0)
        );
        if (gained.length > 0) {
          // バナー種別を判定（予約 / 追加配送 / 両方）
          const hasReservation = gained.some(k => k === 'today_consultations' || k === 'unassigned_pool');
          const hasRefill      = gained.some(k => k === 'shipping_pending');
          setBannerType(hasReservation && hasRefill ? 'both' : hasRefill ? 'refill' : 'reservation');
          setNewReservationBanner(true);
          // 増加したカテゴリを自動展開
          setOpenKeys(prev => {
            const next = new Set(prev);
            gained.forEach(k => next.add(k));
            return next;
          });
        }
      }
      // 現在のカウントを保存
      (Object.keys(d) as (keyof PendingTasksData)[]).forEach(k => {
        prevCountsRef.current[k] = d[k].count;
      });

      setData(d);
      // 初回ロード時のみ自動展開（手動折りたたみ状態を保持）
      if (!autoExpandedRef.current) {
        autoExpandedRef.current = true;
        const autoOpen = new Set<string>();
        Object.entries(d).forEach(([key, cat]) => {
          if (cat.count > 0) autoOpen.add(key);
        });
        setOpenKeys(autoOpen);
      }
    } catch {
      setError(true);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── バックグラウンド自動ポーリング ────────────────────────────────
  useEffect(() => {
    const timer = setInterval(() => {
      // タブ非表示時はスキップ
      if (document.visibilityState !== 'visible') return;
      load(true);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const toggleKey = (key: string) => {
    setOpenKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); } else { next.add(key); }
      return next;
    });
  };

  const lang = i18n?.language?.toLowerCase?.() || 'ja';
  const isZh = lang.startsWith('zh');

  const openTracking = (item: TaskItem) => {
    setTrackingItemId(item.id);
    setTrackingNumber('');
    setTrackingCarrier('yamato');
  };

  const handleTrackingSubmit = async (itemId: number) => {
    if (!trackingNumber.trim() || trackingSubmittingRef.current) return;
    trackingSubmittingRef.current = true;
    setTrackingSubmitting(true);
    const prefix = role === 'admin' ? '/admin' : '/doctor';
    try {
      await api.patch(`${prefix}/reservations/${itemId}/tracking`, {
        tracking_number: trackingNumber.trim(),
        shipping_carrier: trackingCarrier,
      });
      setTrackingItemId(null);
      await load();
    } catch (e: any) {
      alert(e?.response?.data?.detail || t('pending_tasks.tracking_failed'));
    } finally {
      trackingSubmittingRef.current = false;
      setTrackingSubmitting(false);
    }
  };

  const handleClaim = async () => {
    if (!claimTarget || claiming) return;
    setClaiming(true);
    try {
      await api.post(`/doctor/reservations/${claimTarget.id}/claim`);
      setClaimTarget(null);
      await load();
      onClaimSuccess?.();
    } catch (e: any) {
      alert(e?.response?.data?.detail || t('doctor.claim_failed') || '取得に失敗しました。');
    } finally {
      setClaiming(false);
    }
  };

  // ── カテゴリ定義（表示順）── role / isZh が変わったときだけ再生成
  const categories = useMemo<Array<{
    key: keyof PendingTasksData;
    icon: string;
    action?: (item: TaskItem) => React.ReactNode;
    emptyAction?: () => React.ReactNode;  // items=[] だが count>0 のときに表示する fallback
  }>>(() => [
    {
      key: 'today_consultations',
      icon: '🏥',
      action: (item) =>
        role === 'doctor' ? (
          <button
            onClick={() => navigate(`/doctor/consultation/${item.id}`)}
            className="px-3 py-1 text-xs bg-coral text-white rounded-lg font-semibold hover:bg-coral/90 transition whitespace-nowrap"
          >
            {t('pending_tasks.action_enter')}
          </button>
        ) : null,
    },
    {
      key: 'unassigned_pool',
      icon: '🧺',
      action: (item) =>
        role === 'doctor' ? (
          <button
            onClick={() => setClaimTarget(item)}
            className="px-3 py-1 text-xs bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition whitespace-nowrap"
          >
            {t('pending_tasks.action_claim')}
          </button>
        ) : null,
    },
    {
      key: 'record_pending',
      icon: '📝',
      action: (item) =>
        role === 'doctor' ? (
          <button
            onClick={() => navigate(`/doctor/record/${item.id}`)}
            className="px-3 py-1 text-xs bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition whitespace-nowrap"
          >
            {t('pending_tasks.action_record')}
          </button>
        ) : null,
    },
    {
      key: 'questionnaire_pending',
      icon: '📋',
      action: () => null,
    },
    {
      key: 'shipping_pending',
      icon: '📦',
      action: (item) => (
        <button
          onClick={() => openTracking(item)}
          className="flex items-center gap-1 px-3 py-1 text-xs bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition whitespace-nowrap"
        >
          <Package size={12} />
          {t('pending_tasks.action_tracking')}
        </button>
      ),
      emptyAction: () => (
        <button
          onClick={() => navigate(role === 'admin' ? '/admin/reservations' : '/doctor/reservations')}
          className="px-3 py-1 text-xs bg-gray-500 text-white rounded-lg font-semibold hover:bg-gray-600 transition whitespace-nowrap"
        >
          {t('pending_tasks.view_in_list')}
        </button>
      ),
    },
    {
      key: 'payment_pending',
      icon: '💴',
      action: (_item) => (
        <button
          onClick={() => navigate(role === 'admin' ? '/admin/reservations' : '/doctor/reservations')}
          className="px-3 py-1 text-xs bg-rose-600 text-white rounded-lg font-semibold hover:bg-rose-700 transition whitespace-nowrap"
        >
          {t('pending_tasks.action_reservations')}
        </button>
      ),
      emptyAction: () => (
        <button
          onClick={() => navigate(role === 'admin' ? '/admin/reservations' : '/doctor/reservations')}
          className="px-3 py-1 text-xs bg-rose-600 text-white rounded-lg font-semibold hover:bg-rose-700 transition whitespace-nowrap"
        >
          {t('pending_tasks.action_reservations')}
        </button>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [role, isZh, t, navigate, openTracking, setClaimTarget]);

  const totalCount = data
    ? Object.values(data).reduce((sum, cat) => sum + cat.count, 0)
    : 0;

  return (
    <div>
      {/* ヘッダー */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-800">
          ⚡ {t('pending_tasks.title')}
          {!loading && totalCount > 0 && (
            <span className="ml-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold bg-red-100 text-red-600 rounded-full">
              {totalCount}
            </span>
          )}
        </h3>
        <button
          onClick={() => load(false)}
          disabled={loading}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition"
          title={t('pending_tasks.refresh')}
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          {t('pending_tasks.refresh')}
        </button>
      </div>

      {/* 新規予約バナー */}
      {newReservationBanner && (
        <div className="flex items-center justify-between gap-2 mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700 animate-pulse-once">
          <div className="flex items-center gap-2">
            <Bell size={14} className="shrink-0" />
            <span className="font-semibold">
              {bannerType === 'refill'      && t('pending_tasks.new_refill')}
              {bannerType === 'reservation' && t('pending_tasks.new_reservation')}
              {bannerType === 'both'        && t('pending_tasks.new_both')}
            </span>
          </div>
          <button
            onClick={() => setNewReservationBanner(false)}
            className="text-blue-400 hover:text-blue-600 transition text-lg leading-none shrink-0"
            aria-label="dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* エラー */}
      {error && (
        <div className="text-sm text-red-500 py-2">
          {t('pending_tasks.load_failed')}
        </div>
      )}

      {/* ローディング */}
      {loading && !data && (
        <div className="text-sm text-gray-400 py-2">
          {t('pending_tasks.loading')}
        </div>
      )}

      {/* 全件 0 */}
      {!loading && !error && totalCount === 0 && (
        <div className="flex items-center gap-2 py-3 text-sm text-emerald-600">
          <CheckCircle2 size={16} />
          {t('pending_tasks.all_done')}
        </div>
      )}

      {/* カテゴリ一覧 */}
      {data && totalCount > 0 && (
        <div className="space-y-2">
          {categories.map(({ key, icon, action, emptyAction }) => {
            const cat = data[key];
            const isOpen = openKeys.has(key);
            return (
              <div key={key} className="border border-gray-100 rounded-xl overflow-hidden">
                {/* カテゴリヘッダー */}
                <button
                  onClick={() => toggleKey(key)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition text-left"
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                    <span>{icon}</span>
                    {t(`pending_tasks.${key}`)}
                    <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs rounded-full ${badgeClass(cat.count)}`}>
                      {cat.count}
                    </span>
                  </span>
                  {isOpen
                    ? <ChevronUp size={14} className="text-gray-400 shrink-0" />
                    : <ChevronDown size={14} className="text-gray-400 shrink-0" />
                  }
                </button>

                {/* アイテムリスト */}
                {isOpen && cat.items.length > 0 && (
                  <div className="divide-y divide-gray-50">
                    {cat.items.map((item) => (
                      <div key={item.id}>
                        <div className="flex items-center justify-between gap-2 px-3 py-2 bg-white hover:bg-gray-50/50 transition">
                          {/* 左：日付 + 患者名 + サービス */}
                          <div className="flex flex-col min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-gray-500 shrink-0">
                                {item.date} {item.time}
                              </span>
                              <span className="text-sm font-medium text-gray-800 truncate">
                                {item.user_name || '-'}
                              </span>
                            </div>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              <span className="text-xs text-gray-400">
                                {t(`service.${item.service}`, item.service)}
                              </span>
                              {item.is_refill && (
                                <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold bg-teal-50 text-teal-600 border border-teal-200 rounded-full leading-none shrink-0">
                                  {t('pending_tasks.refill_badge')}
                                </span>
                              )}
                              {item.doctor_name && (
                                <span className="text-xs text-gray-400">
                                  👨‍⚕️ {item.doctor_name}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* 右：アクションボタン */}
                          <div className="shrink-0">
                            {action?.(item)}
                          </div>
                        </div>

                        {/* 追跡番号インライン入力フォーム（shipping_pending のみ） */}
                        {key === 'shipping_pending' && trackingItemId === item.id && (
                          <div className="px-3 pb-3 bg-orange-50 border-t border-orange-100">
                            <div className="flex flex-wrap gap-2 pt-2 items-center">
                              <select
                                value={trackingCarrier}
                                onChange={e => setTrackingCarrier(e.target.value)}
                                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-orange-400"
                              >
                                <option value="yamato">{t('pending_tasks.carrier_yamato')}</option>
                                <option value="sagawa">{t('pending_tasks.carrier_sagawa')}</option>
                                <option value="yupack">{t('pending_tasks.carrier_yupack')}</option>
                              </select>
                              <input
                                type="text"
                                value={trackingNumber}
                                onChange={e => setTrackingNumber(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && handleTrackingSubmit(item.id)}
                                placeholder={t('pending_tasks.tracking_placeholder')}
                                className="flex-1 min-w-[140px] text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-orange-400"
                                autoFocus
                              />
                              <button
                                onClick={() => handleTrackingSubmit(item.id)}
                                disabled={!trackingNumber.trim() || trackingSubmitting}
                                className="px-3 py-1.5 text-xs bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50 transition whitespace-nowrap"
                              >
                                {trackingSubmitting ? '...' : t('pending_tasks.tracking_submit')}
                              </button>
                              <button
                                onClick={() => setTrackingItemId(null)}
                                className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition whitespace-nowrap"
                              >
                                {t('pending_tasks.cancel')}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* 件数超過時の「すべて表示」 */}
                    {cat.count > cat.items.length && (
                      <div className="px-3 py-2 text-center bg-white">
                        <span className="text-xs text-gray-400">
                          {t('pending_tasks.view_all')}（{t('doctor.tab_reservations')}）
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* 展開中だがアイテムなし（医師の shipping/payment など） */}
                {isOpen && cat.items.length === 0 && cat.count > 0 && (
                  <div className="flex items-center justify-between px-3 py-2 bg-white">
                    <span className="text-xs text-gray-400">
                      {t('pending_tasks.total_count', { count: cat.count })}
                    </span>
                    {emptyAction?.()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Claim 確認モーダル */}
      {claimTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm shadow-xl">
            <h2 className="text-base font-bold mb-2">
              {t('pending_tasks.claim_confirm_title')}
            </h2>
            <p className="text-sm text-gray-600 mb-1">
              {claimTarget.user_name} — {claimTarget.date} {claimTarget.time}
            </p>
            <p className="text-sm text-gray-500 mb-4">
              {t('pending_tasks.claim_confirm_desc')}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setClaimTarget(null)}
                className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-200 transition"
              >
                {t('cancel')}
              </button>
              <button
                onClick={handleClaim}
                disabled={claiming}
                className="flex-1 px-4 py-2 bg-coral text-white rounded-lg text-sm font-medium hover:bg-coral/90 disabled:opacity-50 transition"
              >
                {claiming
                  ? t('pending_tasks.processing')
                  : t('pending_tasks.action_claim')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingTasksPanel;
